import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../i18n/LanguageContext';
import LanguageSelector from '../components/LanguageSelector';

const API = (process.env.REACT_APP_BACKEND_URL || 'https://bobine.live');

const QUESTION_TYPES_I18N = {
  fr: [{ key: 'qui', label: 'Qui est-ce ?' }, { key: 'ou', label: 'Ou etait-ce ?' }, { key: 'quand', label: 'Quelle annee ?' }, { key: 'libre', label: 'Commentaire libre' }],
  en: [{ key: 'qui', label: 'Who is this?' }, { key: 'ou', label: 'Where was this?' }, { key: 'quand', label: 'What year?' }, { key: 'libre', label: 'Free comment' }],
  es: [{ key: 'qui', label: 'Quien es?' }, { key: 'ou', label: 'Donde fue?' }, { key: 'quand', label: 'Que ano?' }, { key: 'libre', label: 'Comentario libre' }],
};

const georgia = "'Georgia', serif";

/* ── Decorative gold corner for Heritage look ── */
const GoldCorner = ({ position }) => {
  const styles = {
    topLeft: { top: 0, left: 0, borderTop: '2px solid #D4AF37', borderLeft: '2px solid #D4AF37' },
    topRight: { top: 0, right: 0, borderTop: '2px solid #D4AF37', borderRight: '2px solid #D4AF37' },
    bottomLeft: { bottom: 0, left: 0, borderBottom: '2px solid #D4AF37', borderLeft: '2px solid #D4AF37' },
    bottomRight: { bottom: 0, right: 0, borderBottom: '2px solid #D4AF37', borderRight: '2px solid #D4AF37' },
  };
  return <div style={{ position: 'absolute', width: 20, height: 20, ...styles[position], opacity: 0.6, zIndex: 2 }} />;
};

/* ── Gold divider line ── */
const GoldDivider = ({ width = 80 }) => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, margin: '0 auto' }}>
    <div style={{ width: (width - 20) / 2, height: 1, background: 'linear-gradient(90deg, transparent, rgba(212,175,55,0.5))' }} />
    <div style={{ width: 5, height: 5, borderRadius: '50%', backgroundColor: 'rgba(212,175,55,0.4)', boxShadow: '0 0 6px rgba(212,175,55,0.3)' }} />
    <div style={{ width: (width - 20) / 2, height: 1, background: 'linear-gradient(270deg, transparent, rgba(212,175,55,0.5))' }} />
  </div>
);

export default function EclaireurView() {
  const { linkId } = useParams();
  const { t, lang } = useLanguage();
  const QUESTION_TYPES = QUESTION_TYPES_I18N[lang] || QUESTION_TYPES_I18N.fr;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [guestName, setGuestName] = useState(() => localStorage.getItem('eclaireur_name') || '');
  const [commentText, setCommentText] = useState('');
  const [questionType, setQuestionType] = useState('libre');
  const [sending, setSending] = useState(false);
  const [recording, setRecording] = useState(false);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const [deleting, setDeleting] = useState(null);

  useEffect(() => {
    axios.get(`${API}/api/eclaireur/view/${linkId}`)
      .then(res => { setData(res.data); setLoading(false); })
      .catch(() => { setError(lang === 'fr' ? "Ce lien n'est plus actif ou a expire." : lang === 'es' ? "Este enlace ya no esta activo o ha expirado." : "This link is no longer active or has expired."); setLoading(false); });
  }, [linkId, lang]);

  const handleComment = useCallback(async () => {
    if (!commentText.trim() || !selectedPhoto) return;
    setSending(true);
    try {
      const res = await axios.post(`${API}/api/eclaireur/comment/${linkId}`, {
        photo_id: selectedPhoto,
        guest_name: guestName || 'Un Eclaireur',
        text: commentText.trim(),
        question_type: questionType,
      });
      if (res.data.status === 'success') {
        setData(prev => ({ ...prev, comments: [res.data.comment, ...(prev.comments || [])] }));
        setCommentText('');
        localStorage.setItem('eclaireur_name', guestName);
      }
    } catch { /* silent */ }
    setSending(false);
  }, [commentText, selectedPhoto, linkId, guestName, questionType]);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      const chunks = [];
      recorder.ondataavailable = e => { if (e.data.size > 0) chunks.push(e.data); };
      recorder.onstop = async () => {
        stream.getTracks().forEach(tr => tr.stop());
        const blob = new Blob(chunks, { type: 'audio/webm' });
        const reader = new FileReader();
        reader.onload = async () => {
          const audioB64 = reader.result.split(',')[1];
          setSending(true);
          try {
            const res = await axios.post(`${API}/api/eclaireur/comment/${linkId}`, {
              photo_id: selectedPhoto,
              guest_name: guestName || 'Un Eclaireur',
              text: '(message vocal)',
              question_type: questionType,
              audio: audioB64,
            });
            if (res.data.status === 'success') {
              setData(prev => ({ ...prev, comments: [res.data.comment, ...(prev.comments || [])] }));
              localStorage.setItem('eclaireur_name', guestName);
            }
          } catch { /* silent */ }
          setSending(false);
        };
        reader.readAsDataURL(blob);
      };
      recorder.start();
      setMediaRecorder(recorder);
      setRecording(true);
    } catch { /* mic not allowed */ }
  };

  const stopRecording = () => {
    if (mediaRecorder && mediaRecorder.state !== 'inactive') {
      mediaRecorder.stop();
      setRecording(false);
    }
  };

  const handleDeleteComment = async (commentId) => {
    setDeleting(commentId);
    try {
      await axios.delete(`${API}/api/eclaireur/comment/${linkId}/${commentId}`);
      setData(prev => ({ ...prev, comments: (prev.comments || []).filter(c => c.id !== commentId) }));
    } catch { /* silent */ }
    setDeleting(null);
  };

  /* ─── Loading ─── */
  if (loading) return (
    <div style={{ minHeight: '100vh', backgroundColor: '#050502', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 16 }}>
      <img src="/bobine-gold-reel.png" alt="" onError={e => { e.target.src = '/bobine-face-logo.png'; }}
        style={{ width: 70, filter: 'drop-shadow(0 0 20px rgba(212,175,55,0.3))', animation: 'spin 4s linear infinite' }} />
      <span style={{ fontFamily: georgia, fontSize: 13, color: '#D4AF37', letterSpacing: 2 }}>BOBINE</span>
    </div>
  );

  /* ─── Error ─── */
  if (error) return (
    <div style={{ minHeight: '100vh', backgroundColor: '#050502', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div style={{ textAlign: 'center', maxWidth: 400, position: 'relative', padding: 40 }}>
        <GoldCorner position="topLeft" /><GoldCorner position="topRight" /><GoldCorner position="bottomLeft" /><GoldCorner position="bottomRight" />
        <img src="/bobine-gold-reel.png" alt="" onError={e => { e.target.src = '/bobine-face-logo.png'; }}
          style={{ width: 60, marginBottom: 20, filter: 'drop-shadow(0 0 12px rgba(212,175,55,0.2))' }} />
        <p style={{ fontFamily: georgia, fontSize: 16, color: '#D4AF37' }}>{error}</p>
      </div>
    </div>
  );

  const { owner_name, owner_phone, message, photos, comments } = data;
  const photoComments = (pid) => (comments || []).filter(c => c.photo_id === pid);
  const totalComments = (comments || []).length;

  const handleWhatsAppReturn = () => {
    const appLink = window.location.origin + '/dashboard';
    const msg = encodeURIComponent(t('ecl_whatsapp_msg') + `\n\n${appLink}`);
    const phone = (owner_phone || '').replace(/[^0-9+]/g, '');
    window.open(phone ? `https://wa.me/${phone}?text=${msg}` : `https://wa.me/?text=${msg}`, '_blank');
  };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#050502', color: '#fff' }}>
      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes fadeInUp { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes pulseGold { 0%, 100% { opacity: 1; box-shadow: 0 0 28px rgba(212,175,55,0.5); } 50% { opacity: 0.7; box-shadow: 0 0 48px rgba(212,175,55,0.7); } }
        @keyframes shimmerGold { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }
        .heritage-photo:hover { transform: scale(1.01); box-shadow: 0 8px 40px rgba(212,175,55,0.15) !important; }
        .heritage-photo { transition: all 0.4s ease; }
      `}</style>

      {/* ═══════ HEADER HERITAGE ═══════ */}
      <header data-testid="eclaireur-header" style={{
        padding: '32px 20px 28px', textAlign: 'center', position: 'relative',
        background: 'linear-gradient(180deg, rgba(5,5,2,1) 0%, rgba(8,7,4,0.98) 60%, rgba(5,5,2,1) 100%)',
        borderBottom: '1px solid rgba(212,175,55,0.15)',
      }}>
        {/* Double gold filet decoratif */}
        <div style={{ position: 'absolute', top: 0, left: '10%', right: '10%', height: 1, background: 'linear-gradient(90deg, transparent, rgba(212,175,55,0.3), transparent)' }} />
        <div style={{ position: 'absolute', top: 3, left: '15%', right: '15%', height: 1, background: 'linear-gradient(90deg, transparent, rgba(212,175,55,0.15), transparent)' }} />

        {/* Logo + Titre */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, marginBottom: 16 }}>
          <img src="/bobine-gold-reel.png" alt="Bobine" onError={e => { e.target.src = '/bobine-face-logo.png'; }}
            style={{ width: 44, height: 44, objectFit: 'contain', filter: 'drop-shadow(0 0 12px rgba(212,175,55,0.3))' }} />
          <div style={{ textAlign: 'left' }}>
            <span style={{ fontFamily: georgia, fontSize: 22, color: '#D4AF37', letterSpacing: 3, fontWeight: 600, display: 'block', textShadow: '0 0 16px rgba(212,175,55,0.2)' }}>BOBINE</span>
            <span style={{ fontFamily: georgia, fontSize: 10, color: '#D4AF37', letterSpacing: 4, textTransform: 'uppercase' }}>Le fil de votre memoire</span>
          </div>
        </div>

        <GoldDivider width={120} />

        {/* Titre principal */}
        <h1 style={{
          fontFamily: georgia, fontSize: 24, color: '#D4AF37', fontWeight: 600,
          marginTop: 16, marginBottom: 8, letterSpacing: 0.5,
          textShadow: '0 0 20px rgba(212,175,55,0.15)',
        }}>
          {t('ecl_title')}
        </h1>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, flexWrap: 'wrap' }}>
          <p style={{ fontFamily: georgia, fontSize: 13, color: '#FAF3E0', lineHeight: 1.6, margin: 0 }}>
            <strong style={{ color: '#D4AF37', fontWeight: 600 }}>{owner_name}</strong> {t('ecl_subtitle')}
          </p>
          <LanguageSelector />
        </div>

        {/* Carte postale Heritage — message d'invitation */}
        {message && (
          <div style={{
            maxWidth: 500, margin: '24px auto 0', padding: '32px 36px', position: 'relative',
            border: '1px solid rgba(212,175,55,0.25)', borderRadius: 2,
            background: 'radial-gradient(ellipse at center, rgba(212,175,55,0.04) 0%, rgba(5,5,2,0.98) 70%)',
          }}>
            <GoldCorner position="topLeft" /><GoldCorner position="topRight" /><GoldCorner position="bottomLeft" /><GoldCorner position="bottomRight" />
            <p style={{
              fontFamily: "'Didot', 'Playfair Display', 'Garamond', Georgia, serif",
              fontSize: 20, color: '#D4AF37', fontStyle: 'italic',
              lineHeight: 1.7, textAlign: 'center', margin: 0,
              textShadow: '0 0 20px rgba(212,175,55,0.12)',
              fontWeight: 400, letterSpacing: 0.3,
            }}>
              "{message}"
            </p>
            <div style={{ marginTop: 16, marginBottom: 2 }}><GoldDivider width={60} /></div>
            <p style={{ fontFamily: georgia, fontSize: 11, color: '#D4AF37', textAlign: 'center', margin: '8px 0 0', fontStyle: 'italic' }}>
              — {owner_name}
            </p>
          </div>
        )}

        {/* Bouton WhatsApp retour */}
        {totalComments > 0 && (
          <button
            data-testid="whatsapp-return-btn"
            onClick={handleWhatsAppReturn}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              marginTop: 20, padding: '10px 24px', borderRadius: 24,
              backgroundColor: '#25D366', color: '#fff', border: 'none',
              fontFamily: georgia, fontSize: 13, fontWeight: 600,
              cursor: 'pointer', transition: 'all 0.3s',
              boxShadow: '0 2px 12px rgba(37,211,102,0.3)',
            }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="#fff">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
            </svg>
            {t('ecl_whatsapp_btn')}
          </button>
        )}
      </header>

      {/* ═══════ NOM DE L'ECLAIREUR ═══════ */}
      <div style={{ padding: '20px 20px 12px', textAlign: 'center' }}>
        <p style={{ fontFamily: georgia, fontSize: 14, color: '#D4AF37', marginBottom: 10, fontWeight: 500, letterSpacing: 0.5 }}>
          {t('ecl_enter_name')}
        </p>
        <input
          data-testid="eclaireur-name-input"
          type="text"
          placeholder={lang === 'fr' ? "Votre prenom (ex : Marie, Papi Jean...)" : lang === 'es' ? "Tu nombre (ej: Maria, Abuelo Juan...)" : "Your first name (e.g. Marie, Grandpa John...)"}
          value={guestName}
          onChange={e => setGuestName(e.target.value)}
          style={{
            fontFamily: georgia, fontSize: 13, color: '#D4AF37',
            backgroundColor: 'rgba(212,175,55,0.03)',
            border: '1px solid rgba(212,175,55,0.2)',
            borderRadius: 4, padding: '10px 18px', outline: 'none',
            textAlign: 'center', width: '100%', maxWidth: 280,
            transition: 'border-color 0.3s',
          }}
          onFocus={e => { e.target.style.borderColor = '#D4AF37'; }}
          onBlur={e => { e.target.style.borderColor = '#D4AF37'; }}
        />
      </div>

      {/* Instruction */}
      {!selectedPhoto && (
        <p data-testid="eclaireur-instruction" style={{
          fontFamily: georgia, fontSize: 13, color: '#D4AF37',
          textAlign: 'center', margin: '0 20px 12px', fontStyle: 'italic',
          animation: 'fadeInUp 0.8s ease-out',
        }}>
          {t('ecl_tap_to_comment')}
        </p>
      )}

      {/* ═══════ GALERIE PHOTOS — STYLE HERITAGE ═══════ */}
      <div style={{
        display: 'flex', flexDirection: 'column', gap: 28,
        padding: '12px 16px 180px', maxWidth: 700, margin: '0 auto',
      }}>
        {photos.map((photo, idx) => {
          const isSelected = selectedPhoto === photo.id;
          const pComments = photoComments(photo.id);
          return (
            <div
              key={photo.id}
              data-testid={`eclaireur-photo-${photo.id}`}
              className="heritage-photo"
              onClick={() => setSelectedPhoto(isSelected ? null : photo.id)}
              style={{
                position: 'relative', cursor: 'pointer',
                borderRadius: 2, overflow: 'hidden',
                border: isSelected ? '2px solid #D4AF37' : '1px solid rgba(212,175,55,0.12)',
                boxShadow: isSelected
                  ? '0 0 40px rgba(212,175,55,0.2), 0 0 80px rgba(212,175,55,0.06)'
                  : '0 4px 30px rgba(0,0,0,0.5)',
                animation: `fadeInUp 0.5s ease-out ${idx * 0.08}s both`,
              }}
            >
              {/* Corniere decoratives Heritage */}
              <GoldCorner position="topLeft" /><GoldCorner position="topRight" />
              <GoldCorner position="bottomLeft" /><GoldCorner position="bottomRight" />

              {/* Photo */}
              <div style={{
                aspectRatio: '3/2', position: 'relative', minHeight: 260,
                background: 'radial-gradient(ellipse at 30% 30%, #1a1714 0%, #0d0b09 50%, #080706 100%)',
              }}>
                <img
                  src={`${API}/api/eclaireur/view/${linkId}/photo/${photo.id}/image`}
                  alt=""
                  loading="lazy"
                  onLoad={e => { e.target.style.opacity = '1'; }}
                  onError={e => { e.target.style.opacity = '0.3'; e.target.src = '/bobine-face-logo.png'; e.target.style.objectFit = 'contain'; e.target.style.padding = '25%'; }}
                  style={{
                    width: '100%', height: '100%', objectFit: 'contain',
                    opacity: 0, transition: 'opacity 0.5s ease',
                    transform: photo.rotation ? `rotate(${photo.rotation}deg)` : 'none',
                  }}
                />
                {/* Badge nombre de commentaires */}
                {pComments.length > 0 && (
                  <div style={{
                    position: 'absolute', top: 12, right: 12,
                    background: 'linear-gradient(145deg, #D4AF37, #B8860B)', color: '#050502',
                    fontFamily: georgia, fontSize: 11, fontWeight: 700,
                    padding: '4px 10px', borderRadius: 2, minWidth: 24, textAlign: 'center',
                    boxShadow: '0 2px 10px rgba(212,175,55,0.3)',
                    letterSpacing: 0.5,
                  }}>
                    {pComments.length}
                  </div>
                )}
                {/* Indicateur de selection Heritage */}
                {isSelected && (
                  <div style={{
                    position: 'absolute', bottom: 14, left: '50%', transform: 'translateX(-50%)',
                    backgroundColor: 'rgba(5,5,2,0.85)', border: '1px solid rgba(212,175,55,0.5)',
                    padding: '7px 20px', borderRadius: 2,
                    boxShadow: '0 4px 20px rgba(0,0,0,0.6)',
                  }}>
                    <span style={{ fontFamily: georgia, fontSize: 11, color: '#D4AF37', letterSpacing: 1.5, textTransform: 'uppercase' }}>
                      {t('ecl_comment_placeholder').split('...')[0]}...
                    </span>
                  </div>
                )}
              </div>

              {/* Legende Heritage */}
              <div style={{
                padding: '12px 16px', position: 'relative',
                backgroundColor: 'rgba(5,5,2,0.95)',
                borderTop: '1px solid rgba(212,175,55,0.1)',
              }}>
                <p style={{
                  fontFamily: georgia, fontSize: 12, color: '#D4AF37',
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  fontStyle: 'italic', letterSpacing: 0.3, margin: 0,
                }}>
                  {photo.caption || (photo.taken_at ? new Date(photo.taken_at).toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' }) : photo.filename)}
                </p>
              </div>
            </div>
          );
        })}
      </div>

      {/* ═══════ PANNEAU DE COMMENTAIRES — HERITAGE ═══════ */}
      {selectedPhoto && (
        <div data-testid="eclaireur-comment-panel" style={{
          position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 9999,
          backgroundColor: 'rgba(5,5,2,0.98)',
          borderTop: '1px solid rgba(212,175,55,0.25)',
          backdropFilter: 'blur(16px)',
          padding: '18px 20px 34px 20px', maxHeight: '50vh', overflowY: 'auto',
        }}>
          {/* Filet decoratif en haut */}
          <div style={{ position: 'absolute', top: 0, left: '10%', right: '10%', height: 1, background: 'linear-gradient(90deg, transparent, rgba(212,175,55,0.2), transparent)' }} />

          {/* Commentaires existants */}
          {photoComments(selectedPhoto).length > 0 && (
            <div style={{ marginBottom: 14 }}>
              {photoComments(selectedPhoto).map(c => (
                <div key={c.id} style={{
                  padding: '10px 14px', marginBottom: 8, borderRadius: 2,
                  backgroundColor: 'rgba(212,175,55,0.03)',
                  border: '1px solid rgba(212,175,55,0.1)',
                  display: 'flex', alignItems: 'flex-start', gap: 10,
                  position: 'relative',
                }}>
                  <div style={{ flex: 1 }}>
                    <span style={{ fontFamily: georgia, fontSize: 12, color: '#D4AF37', fontWeight: 600, letterSpacing: 0.3 }}>
                      {c.guest_name}
                    </span>
                    <span style={{ fontFamily: georgia, fontSize: 10, color: '#D4AF37', marginLeft: 10 }}>
                      {QUESTION_TYPES.find(q => q.key === c.question_type)?.label || ''}
                    </span>
                    <p style={{ fontFamily: georgia, fontSize: 12, color: '#FAF3E0', marginTop: 4, lineHeight: 1.5, margin: '4px 0 0' }}>
                      {c.has_audio ? (
                        <span style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                          <button
                            data-testid={`play-audio-${c.id}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              const btn = e.currentTarget;
                              if (btn._audio && !btn._audio.paused) {
                                btn._audio.pause(); btn._audio.currentTime = 0; btn._audio = null;
                                btn.querySelector('span').textContent = t('ecl_listen');
                                return;
                              }
                              const audio = new Audio(`${API}/api/eclaireur/audio/${c.id}`);
                              btn._audio = audio;
                              btn.querySelector('span').textContent = t('ecl_stop');
                              audio.play();
                              audio.onended = () => { btn._audio = null; btn.querySelector('span').textContent = t('ecl_listen'); };
                            }}
                            style={{
                              display: 'inline-flex', alignItems: 'center', gap: 6,
                              fontFamily: georgia, fontSize: 12, color: '#D4AF37',
                              background: 'rgba(212,175,55,0.06)', border: '1px solid rgba(212,175,55,0.25)',
                              borderRadius: 2, padding: '5px 14px', cursor: 'pointer',
                              boxShadow: '0 0 8px rgba(212,175,55,0.08)', width: 'fit-content',
                            }}
                          >
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="#D4AF37" stroke="none"><polygon points="5 3 19 12 5 21 5 3"/></svg>
                            <span>{t('ecl_listen')}</span>
                          </button>
                          {c.text && c.text !== '(message vocal)' && (
                            <button
                              onClick={(e) => { e.stopPropagation(); const p = e.currentTarget.nextElementSibling; if (p) p.style.display = p.style.display === 'none' ? 'block' : 'none'; }}
                              style={{ fontFamily: georgia, fontSize: 11, color: '#D4AF37', background: '#D4AF37', border: '1px solid rgba(212,175,55,0.2)', borderRadius: 2, padding: '3px 12px', cursor: 'pointer', width: 'fit-content', display: 'inline-flex', alignItems: 'center', gap: 4 }}
                            >
                              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#D4AF37" strokeWidth="1.8"><path d="M20.24 12.24a6 6 0 00-8.49-8.49L5 10.5V19h8.5z"/><line x1="16" y1="8" x2="2" y2="22"/></svg>
                              {t('ecl_transcription')}
                            </button>
                          )}
                          {c.text && c.text !== '(message vocal)' && (
                            <span style={{ fontStyle: 'italic', color: '#FAF3E0', fontSize: 11.5, lineHeight: 1.5, display: 'none' }}>
                              "{c.text}"
                            </span>
                          )}
                        </span>
                      ) : c.text}
                    </p>
                  </div>
                  {/* Delete button Heritage */}
                  <button
                    data-testid={`delete-comment-${c.id}`}
                    onClick={(e) => { e.stopPropagation(); handleDeleteComment(c.id); }}
                    disabled={deleting === c.id}
                    style={{
                      flexShrink: 0, width: 32, height: 32, borderRadius: 2,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      cursor: deleting === c.id ? 'not-allowed' : 'pointer',
                      backgroundColor: 'rgba(212,175,55,0.06)', border: '1px solid rgba(212,175,55,0.3)',
                      opacity: deleting === c.id ? 0.4 : 1, transition: 'all 0.2s',
                    }}
                    title={t('dash_delete')}
                  >
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#D4AF37" strokeWidth="2" strokeLinecap="round">
                      <polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/>
                    </svg>
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Selecteur de type de question */}
          <div style={{ display: 'flex', gap: 6, marginBottom: 12, flexWrap: 'wrap' }}>
            {QUESTION_TYPES.map(q => (
              <button
                key={q.key}
                data-testid={`question-type-${q.key}`}
                onClick={() => setQuestionType(q.key)}
                style={{
                  fontFamily: georgia, fontSize: 11, padding: '6px 14px', borderRadius: 2,
                  cursor: 'pointer', border: '1px solid rgba(212,175,55,0.2)',
                  backgroundColor: questionType === q.key ? 'rgba(212,175,55,0.12)' : 'transparent',
                  color: questionType === q.key ? '#D4AF37' : '#D4AF37',
                  transition: 'all 0.25s', letterSpacing: 0.3,
                }}
              >
                {q.label}
              </button>
            ))}
          </div>

          {/* Input commentaire + Micro vocal Heritage */}
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {/* Micro dore Heritage */}
            <button
              data-testid="eclaireur-mic-btn"
              onClick={recording ? stopRecording : startRecording}
              disabled={!selectedPhoto || sending}
              style={{
                width: 52, height: 52, borderRadius: 2, flexShrink: 0,
                cursor: (!selectedPhoto || sending) ? 'not-allowed' : 'pointer',
                background: recording
                  ? 'radial-gradient(circle at 40% 35%, rgba(212,175,55,0.3) 0%, rgba(180,148,46,0.1) 60%, rgba(5,5,2,0.95) 100%)'
                  : 'radial-gradient(circle at 40% 35%, rgba(212,175,55,0.1) 0%, rgba(5,5,2,0.95) 100%)',
                border: recording ? '2px solid #D4AF37' : '1px solid rgba(212,175,55,0.35)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'all 0.3s ease',
                boxShadow: recording
                  ? '0 0 28px rgba(212,175,55,0.5), 0 0 6px rgba(212,175,55,0.3) inset'
                  : '0 0 12px rgba(212,175,55,0.1)',
                animation: recording ? 'pulseGold 1.5s ease-in-out infinite' : 'none',
              }}
            >
              {recording ? (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="#D4AF37" stroke="none"><rect x="6" y="6" width="12" height="12" rx="1"/></svg>
              ) : (
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                  <defs><linearGradient id="micGoldH" x1="0%" y1="0%" x2="0%" y2="100%"><stop offset="0%" stopColor="#F5D778"/><stop offset="50%" stopColor="#D4AF37"/><stop offset="100%" stopColor="#B8942E"/></linearGradient></defs>
                  <path d="M12 1a3 3 0 00-3 3v8a3 3 0 006 0V4a3 3 0 00-3-3z" fill="url(#micGoldH)" opacity="0.3" stroke="url(#micGoldH)" strokeWidth="1.5"/>
                  <path d="M19 10v2a7 7 0 01-14 0v-2" stroke="url(#micGoldH)" strokeWidth="1.5" strokeLinecap="round" fill="none"/>
                  <line x1="12" y1="19" x2="12" y2="23" stroke="url(#micGoldH)" strokeWidth="1.5" strokeLinecap="round"/>
                  <line x1="8" y1="23" x2="16" y2="23" stroke="url(#micGoldH)" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
              )}
            </button>
            {recording ? (
              <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ width: 10, height: 10, borderRadius: 2, background: 'linear-gradient(145deg, #D4AF37, #B8860B)', animation: 'pulseGold 1s ease-in-out infinite', boxShadow: '0 0 8px rgba(212,175,55,0.5)' }}/>
                <span style={{ fontFamily: georgia, fontSize: 13, color: '#D4AF37', fontWeight: 500, letterSpacing: 0.5 }}>
                  {t('audio_recording')}
                </span>
              </div>
            ) : (
              <>
                <input
                  data-testid="eclaireur-comment-input"
                  type="text"
                  placeholder={t('ecl_comment_placeholder')}
                  value={commentText}
                  onChange={e => setCommentText(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') handleComment(); }}
                  style={{
                    flex: 1, fontFamily: georgia, fontSize: 13, color: '#D4AF37',
                    backgroundColor: 'rgba(212,175,55,0.03)', border: '1px solid rgba(212,175,55,0.2)',
                    borderRadius: 2, padding: '12px 16px', outline: 'none',
                    transition: 'border-color 0.3s',
                  }}
                  onFocus={e => { e.target.style.borderColor = '#D4AF37'; }}
                  onBlur={e => { e.target.style.borderColor = '#D4AF37'; }}
                />
                <button
                  data-testid="eclaireur-send-btn"
                  onClick={handleComment}
                  disabled={!commentText.trim() || sending}
                  style={{
                    fontFamily: georgia, fontSize: 13, padding: '12px 22px', borderRadius: 2,
                    cursor: (!commentText.trim() || sending) ? 'not-allowed' : 'pointer',
                    background: 'linear-gradient(145deg, #D4AF37, #B8860B)', color: '#050502', border: 'none',
                    fontWeight: 700, letterSpacing: 0.5,
                    opacity: (!commentText.trim() || sending) ? 0.4 : 1,
                    transition: 'all 0.25s',
                    boxShadow: '0 2px 12px rgba(212,175,55,0.25)',
                  }}
                >
                  {sending ? '...' : t('ecl_send_comment')}
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
